'use client';
import { useState, useEffect, useCallback } from 'react';

export type Language = 'ar' | 'en';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; dir: 'rtl' | 'ltr' }[] = [
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'en', label: 'English', dir: 'ltr' },
];

export const LANG_KEY = 'nexora_lang';

export function getStoredLanguage(): Language {
  if (typeof window === 'undefined') return 'ar';
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'en' || stored === 'ar') return stored;
  } catch {}
  return 'ar';
}

export function setStoredLanguage(lang: Language): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {}
}

// ── Translation Dictionaries ──────────────────────────────────────────────────
const translations: Record<Language, Record<string, string>> = {
  ar: {
    // Navigation
    'nav.dashboard': 'لوحة التحكم',
    'nav.pos': 'نقطة البيع',
    'nav.pos_checkout': 'الدفع والمبيعات',
    'nav.pos_transactions': 'معاملات POS',
    'nav.pos_terminals': 'طرفيات POS',
    'nav.pos_cashier': 'كاشير نقطة البيع',
    'nav.products': 'المنتجات والخدمات',
    'nav.inventory': 'إدارة المخزون',
    'nav.purchases': 'أوامر الشراء',
    'nav.returns': 'المرتجعات',
    'nav.barcode_labels': 'ملصقات الباركود',
    'nav.customer_management': 'إدارة العملاء',
    'nav.customers': 'العملاء والموردون',
    'nav.suppliers': 'الموردون',
    'nav.manufacturing': 'التصنيع والإنتاج',
    'nav.employees': 'ملف الموظفين والرواتب',
    'nav.accounting': 'المحاسبة والحسابات',
    'nav.cash_management': 'الصندوق والورديات',
    'nav.vouchers': 'السندات المحاسبية',
    'nav.vat_center': 'مركز ضريبة القيمة المضافة',
    'nav.reports': 'التقارير والامتثال',
    'nav.invoice_history': 'سجل الفواتير',
    'nav.invoice_archive': 'أرشيف الفواتير',
    'nav.zatca': 'امتثال زاتكا',
    'nav.subscription': 'إدارة الاشتراك',
    'nav.payment_methods': 'طرق الدفع',
    'nav.pricing': 'الباقات والأسعار',
    'nav.legal': 'الوثائق القانونية',
    'nav.branches': 'الفروع',
    'nav.approvals': 'الموافقات',
    'nav.audit_logs': 'سجل التدقيق',
    'nav.backup': 'النسخ الاحتياطي',
    'nav.csv_import': 'استيراد CSV',
    'nav.settings': 'الإعدادات',
    'nav.system_health': 'صحة النظام',
    // Common
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.delete': 'حذف',
    'common.edit': 'تعديل',
    'common.add': 'إضافة',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.export': 'تصدير',
    'common.print': 'طباعة',
    'common.close': 'إغلاق',
    'common.confirm': 'تأكيد',
    'common.loading': 'جاري التحميل...',
    'common.no_data': 'لا توجد بيانات',
    'common.total': 'الإجمالي',
    'common.status': 'الحالة',
    'common.date': 'التاريخ',
    'common.amount': 'المبلغ',
    'common.description': 'الوصف',
    'common.reference': 'المرجع',
    'common.notes': 'ملاحظات',
    'common.actions': 'الإجراءات',
    'common.view': 'عرض',
    'common.back': 'رجوع',
    'common.next': 'التالي',
    'common.previous': 'السابق',
    'common.yes': 'نعم',
    'common.no': 'لا',
    'common.success': 'تم بنجاح',
    'common.error': 'خطأ',
    'common.warning': 'تحذير',
    'common.info': 'معلومة',
    'common.required': 'مطلوب',
    'common.optional': 'اختياري',
    'common.sar': 'ر.س',
    'common.undo': 'تراجع',
    'common.void': 'إلغاء',
    'common.reverse': 'عكس',
    'common.reopen': 'إعادة فتح',
    'common.approve': 'اعتماد',
    'common.reject': 'رفض',
    'common.pending': 'معلق',
    'common.approved': 'معتمد',
    'common.rejected': 'مرفوض',
    'common.active': 'نشط',
    'common.inactive': 'غير نشط',
    'common.all': 'الكل',
    'common.new': 'جديد',
    'common.draft': 'مسودة',
    'common.posted': 'مرحّل',
    'common.cancelled': 'ملغي',
    'common.reversed': 'معكوس',
    'common.refresh': 'تحديث',
    'common.details': 'تفاصيل',
    'common.name': 'الاسم',
    'common.type': 'النوع',
    'common.branch': 'الفرع',
    'common.today': 'اليوم',
    'common.week': 'هذا الأسبوع',
    'common.month': 'هذا الشهر',
    'common.quarter': 'هذا الربع',
    'common.year': 'هذا العام',
    'common.connected': 'متصل',
    'common.disconnected': 'غير متصل',
    'common.online': 'متصل',
    'common.offline': 'غير متصل',
    'common.open': 'مفتوح',
    'common.closed': 'مغلق',
    'common.suspended': 'معلق',
    'common.exit': 'خروج',
    'common.copy': 'نسخ',
    'common.copied': 'تم النسخ',
    'common.enable': 'تفعيل',
    'common.disable': 'تعطيل',
    'common.transactions': 'معاملات',
    'common.sales': 'مبيعات',
    'common.purchases': 'مشتريات',
    'common.profit': 'ربح',
    'common.customers': 'عملاء',
    'common.stock': 'مخزون',
    'common.invoice': 'فاتورة',
    'common.invoices': 'فواتير',
    'common.payment': 'دفع',
    'common.cash': 'نقدي',
    'common.card': 'بطاقة',
    'common.transfer': 'تحويل',
    'common.discount': 'خصم',
    'common.vat': 'ضريبة القيمة المضافة',
    'common.subtotal': 'المجموع قبل الضريبة',
    'common.grand_total': 'الإجمالي الكلي',
    'common.change': 'الباقي',
    'common.paid': 'مدفوع',
    'common.refunded': 'مسترجع',
    'common.completed': 'مكتمل',
    // Dashboard
    'dashboard.title': 'لوحة التحكم',
    'dashboard.welcome': 'مرحباً بك — Nexora ERP متوافق مع زاتكا المرحلة الثانية',
    'dashboard.date_range': 'نطاق التاريخ',
    'dashboard.detailed_reports': 'التقارير التفصيلية',
    'dashboard.quick_access': 'وصول سريع للتقارير التفصيلية',
    'dashboard.metrics.today_sales': 'مبيعات اليوم',
    'dashboard.metrics.purchases': 'المشتريات',
    'dashboard.metrics.net_profit': 'صافي الربح',
    'dashboard.metrics.vat': 'ضريبة القيمة المضافة',
    'dashboard.metrics.customers': 'إجمالي العملاء',
    'dashboard.metrics.low_stock': 'مخزون منخفض',
    'dashboard.metrics.invoices_today': 'فاتورة اليوم',
    'dashboard.metrics.purchase_orders': 'أمر شراء',
    'dashboard.metrics.profit_margin': 'هامش الربح',
    'dashboard.metrics.zatca_compliant': 'متوافق مع زاتكا',
    'dashboard.metrics.registered_customers': 'عميل مسجل',
    'dashboard.metrics.below_minimum': 'صنف تحت الحد الأدنى',
    'dashboard.metrics.needs_order': 'يحتاج طلبية',
    'dashboard.metrics.good_level': 'مستوى جيد',
    'dashboard.metrics.new': 'جديد',
    'dashboard.metrics.details': 'تفاصيل',
    'dashboard.drill.sales_title': 'تفاصيل مبيعات اليوم',
    'dashboard.drill.purchases_title': 'تفاصيل المشتريات',
    'dashboard.drill.profit_title': 'تحليل الربحية',
    'dashboard.drill.vat_title': 'تفاصيل ضريبة القيمة المضافة',
    'dashboard.drill.customers_title': 'تحليل العملاء',
    'dashboard.drill.stock_title': 'تنبيهات المخزون',
    'dashboard.drill.view_full_report': 'عرض التقرير الكامل',
    'dashboard.reports.sales': 'تقرير المبيعات',
    'dashboard.reports.sales_sub': 'مبيعات وأرباح',
    'dashboard.reports.purchases': 'تقرير المشتريات',
    'dashboard.reports.purchases_sub': 'موردون وأوامر',
    'dashboard.reports.inventory': 'تقرير المخزون',
    'dashboard.reports.inventory_sub': 'حركة وتحليل',
    'dashboard.reports.vat': 'تقرير الضريبة',
    'dashboard.reports.vat_sub': 'مخرجات ومدخلات',
    'dashboard.reports.financial': 'التقرير المالي',
    'dashboard.reports.financial_sub': 'إيرادات ومصروفات',
    'dashboard.reports.payroll': 'تقرير الرواتب',
    'dashboard.reports.payroll_sub': 'رواتب وتأمينات',
    // Dashboard Accordion sections
    'dashboard.section.overview': 'نظرة عامة — المؤشرات الرئيسية',
    'dashboard.section.charts': 'الرسوم البيانية والامتثال',
    'dashboard.section.activity': 'النشاط والمخزون',
    'dashboard.section.reports': 'وصول سريع للتقارير',
    // POS
    'pos.title': 'نقطة البيع',
    'pos.search_products': 'بحث عن منتج أو باركود...',
    'pos.cart': 'سلة المشتريات',
    'pos.cart_empty': 'السلة فارغة',
    'pos.add_to_cart': 'إضافة للسلة',
    'pos.checkout': 'إتمام البيع',
    'pos.payment': 'الدفع',
    'pos.cash_payment': 'دفع نقدي',
    'pos.card_payment': 'دفع بطاقة',
    'pos.stc_payment': 'STC Pay',
    'pos.split_payment': 'دفع مختلط',
    'pos.bank_transfer': 'تحويل بنكي',
    'pos.credit': 'آجل',
    'pos.amount_paid': 'المبلغ المدفوع',
    'pos.change': 'الباقي',
    'pos.print_receipt': 'طباعة الإيصال',
    'pos.new_sale': 'بيع جديد',
    'pos.hold': 'تعليق',
    'pos.recall': 'استرجاع',
    'pos.discount': 'خصم',
    'pos.customer': 'العميل',
    'pos.cash_customer': 'عميل نقدي',
    'pos.select_customer': 'اختر عميل',
    'pos.barcode': 'باركود',
    'pos.qty': 'الكمية',
    'pos.unit_price': 'سعر الوحدة',
    'pos.subtotal': 'المجموع',
    'pos.vat': 'ضريبة القيمة المضافة',
    'pos.total': 'الإجمالي',
    'pos.invoice_no': 'رقم الفاتورة',
    'pos.categories.all': 'الكل',
    'pos.out_of_stock': 'نفد المخزون',
    'pos.low_stock': 'مخزون منخفض',
    'pos.format_80mm': 'إيصال 80 مم',
    'pos.format_a4': 'فاتورة A4',
    'pos.sale_complete': 'تمت عملية البيع',
    'pos.connected': 'متصل',
    'pos.search_shortcut': 'بحث',
    'pos.pay_shortcut': 'دفع',
    'pos.clear_shortcut': 'مسح',
    'pos.return_to_terminals': 'العودة إلى إدارة الطرفيات',
    // POS Terminals
    'terminals.title': 'طرفيات نقطة البيع',
    'terminals.add': 'إضافة طرفية',
    'terminals.edit': 'تعديل الطرفية',
    'terminals.delete': 'حذف الطرفية',
    'terminals.code': 'رمز الطرفية',
    'terminals.name_ar': 'الاسم بالعربي',
    'terminals.name_en': 'الاسم بالإنجليزي',
    'terminals.branch': 'الفرع',
    'terminals.type': 'نوع الطرفية',
    'terminals.printer': 'الطابعة',
    'terminals.payment_methods': 'طرق الدفع',
    'terminals.shift_status': 'حالة الوردية',
    'terminals.today_sales': 'مبيعات اليوم',
    'terminals.today_transactions': 'معاملات اليوم',
    'terminals.last_activity': 'آخر نشاط',
    'terminals.open_cashier': 'فتح الكاشير',
    'terminals.cashier_url': 'رابط الكاشير',
    'terminals.type.standard': 'كاشير قياسي',
    'terminals.type.mobile': 'كاشير متنقل',
    'terminals.type.kiosk': 'كشك ذاتي',
    'terminals.type.drive_thru': 'درايف ثرو',
    'terminals.printer.thermal_80mm': 'حرارية 80 مم',
    'terminals.printer.thermal_58mm': 'حرارية 58 مم',
    'terminals.printer.a4': 'طابعة A4',
    'terminals.printer.none': 'بدون طابعة',
    'terminals.shift.open': 'وردية مفتوحة',
    'terminals.shift.closed': 'وردية مغلقة',
    'terminals.shift.suspended': 'وردية معلقة',
    'terminals.payment.cash': 'نقدي',
    'terminals.payment.card': 'بطاقة',
    'terminals.payment.stc': 'STC Pay',
    'terminals.payment.mada': 'مدى',
    'terminals.payment.apple_pay': 'Apple Pay',
    'terminals.name_required': 'اسم الطرفية بالعربي مطلوب',
    'terminals.limit_reached': 'وصلت للحد الأقصى من الطرفيات في باقتك',
    'terminals.upgrade': 'ترقية الباقة',
    // POS Transactions
    'pos_tx.title': 'معاملات نقطة البيع',
    'pos_tx.search': 'بحث برقم الفاتورة أو العميل...',
    'pos_tx.all_statuses': 'كل الحالات',
    'pos_tx.all_payments': 'كل طرق الدفع',
    'pos_tx.invoice_no': 'رقم الفاتورة',
    'pos_tx.customer': 'العميل',
    'pos_tx.cashier': 'الكاشير',
    'pos_tx.items': 'أصناف',
    'pos_tx.payment_method': 'طريقة الدفع',
    'pos_tx.total': 'الإجمالي',
    'pos_tx.vat': 'الضريبة',
    'pos_tx.discount': 'الخصم',
    'pos_tx.net': 'الصافي',
    'pos_tx.status.completed': 'مكتمل',
    'pos_tx.status.pending': 'معلق',
    'pos_tx.status.cancelled': 'ملغي',
    'pos_tx.status.refunded': 'مسترجع',
    'pos_tx.payment.cash': 'نقدي',
    'pos_tx.payment.card': 'بطاقة',
    'pos_tx.payment.stc': 'STC Pay',
    'pos_tx.payment.split': 'مختلط',
    'pos_tx.summary.total_sales': 'إجمالي المبيعات',
    'pos_tx.summary.total_vat': 'إجمالي الضريبة',
    'pos_tx.summary.total_discount': 'إجمالي الخصومات',
    'pos_tx.summary.transactions': 'عدد المعاملات',
    // Shifts
    'shift.open': 'فتح وردية',
    'shift.close': 'إغلاق وردية',
    'shift.suspend': 'تعليق وردية',
    'shift.resume': 'استئناف وردية',
    'shift.opening_balance': 'رصيد الافتتاح',
    'shift.closing_balance': 'رصيد الإغلاق',
    'shift.cash_sales': 'مبيعات نقدية',
    'shift.card_sales': 'مبيعات بطاقة',
    'shift.transfer_sales': 'مبيعات تحويل',
    'shift.returns': 'مرتجعات',
    'shift.discounts': 'خصومات',
    'shift.expenses': 'مصاريف',
    'shift.deposits': 'إيداعات',
    'shift.withdrawals': 'سحوبات',
    'shift.expected_cash': 'الرصيد المتوقع',
    'shift.actual_cash': 'الرصيد الفعلي',
    'shift.variance': 'الفرق',
    'shift.manager_pin': 'رمز المدير',
    'shift.cashier': 'الكاشير',
    'shift.branch': 'الفرع',
    'shift.open_time': 'وقت الفتح',
    'shift.close_time': 'وقت الإغلاق',
    'shift.report': 'تقرير الوردية',
    'shift.history': 'سجل الورديات',
    'shift.status.open': 'مفتوح',
    'shift.status.closed': 'مغلق',
    'shift.status.suspended': 'معلق',
    // Accounting
    'accounting.chart_of_accounts': 'دليل الحسابات',
    'accounting.journal_entries': 'القيود اليومية',
    'accounting.general_ledger': 'دفتر الأستاذ',
    'accounting.trial_balance': 'ميزان المراجعة',
    'accounting.balance_sheet': 'الميزانية العمومية',
    'accounting.income_statement': 'قائمة الدخل',
    'accounting.cash_flow': 'التدفقات النقدية',
    'accounting.assets': 'الأصول',
    'accounting.liabilities': 'الالتزامات',
    'accounting.equity': 'حقوق الملكية',
    'accounting.revenue': 'الإيرادات',
    'accounting.expenses': 'المصروفات',
    'accounting.net_profit': 'صافي الربح',
    'accounting.receivables': 'الذمم المدينة',
    'accounting.payables': 'الذمم الدائنة',
    'accounting.cash_position': 'المركز النقدي',
    'accounting.post': 'ترحيل',
    'accounting.unpost': 'إلغاء الترحيل',
    'accounting.batch_post': 'ترحيل دفعي',
    'accounting.debit': 'مدين',
    'accounting.credit': 'دائن',
    // Documents
    'doc.invoice': 'فاتورة',
    'doc.quotation': 'عرض سعر',
    'doc.purchase_order': 'أمر شراء',
    'doc.sales_order': 'أمر بيع',
    'doc.receipt_voucher': 'سند قبض',
    'doc.payment_voucher': 'سند صرف',
    'doc.journal_voucher': 'قيد يومي',
    'doc.cash_voucher': 'سند نقدي',
    'doc.bank_voucher': 'سند بنكي',
    'doc.prepared_by': 'أعده',
    'doc.approved_by': 'اعتمده',
    'doc.received_by': 'استلمه',
    'doc.signature': 'التوقيع',
    'doc.stamp': 'الختم',
    'doc.qr_code': 'رمز QR',
    'doc.vat_number': 'الرقم الضريبي',
    'doc.cr_number': 'السجل التجاري',
    'doc.contract_number': 'رقم العقد',
    'doc.vehicle_number': 'رقم المركبة',
    'doc.sales_rep': 'مندوب المبيعات',
    'doc.customer_ref': 'مرجع العميل',
    'doc.internal_ref': 'المرجع الداخلي',
    // Roles
    'role.super_admin': 'مدير المنصة',
    'role.admin': 'مدير النظام',
    'role.manager': 'مدير المتجر',
    'role.accountant': 'محاسب',
    'role.cashier': 'كاشير',
    'role.viewer': 'مشاهد',
    'role.employee': 'موظف',
    // Company Portal
    'portal.title': 'بوابة الشركة',
    'portal.workspace': 'مساحة العمل',
    'portal.company_profile': 'ملف الشركة',
    'portal.company_url': 'رابط الشركة',
    'portal.branding': 'الهوية البصرية',
    // Validation
    'validation.required_field': 'هذا الحقل مطلوب',
    'validation.invalid_amount': 'المبلغ غير صحيح',
    'validation.balance_mismatch': 'الميزانية غير متوازنة: الأصول ≠ الالتزامات + حقوق الملكية',
    'validation.duplicate_posting': 'تم ترحيل هذا القيد مسبقاً',
    'validation.shift_already_open': 'توجد وردية مفتوحة بالفعل',
    'validation.no_open_shift': 'لا توجد وردية مفتوحة',
    'validation.wrong_pin': 'رمز المدير غير صحيح',
    // Demo
    'demo.banner': 'وضع العرض التجريبي — للمشاهدة فقط، لا يمكن التعديل أو الحذف أو الإنشاء',
    'demo.view_only': 'عرض فقط',
  },
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.pos': 'Point of Sale',
    'nav.pos_checkout': 'POS Checkout',
    'nav.pos_transactions': 'POS Transactions',
    'nav.pos_terminals': 'POS Terminals',
    'nav.pos_cashier': 'POS Cashier',
    'nav.products': 'Products & Services',
    'nav.inventory': 'Inventory Management',
    'nav.purchases': 'Purchase Orders',
    'nav.returns': 'Returns',
    'nav.barcode_labels': 'Barcode Labels',
    'nav.customer_management': 'Customer Management',
    'nav.customers': 'Customers & Vendors',
    'nav.suppliers': 'Suppliers',
    'nav.manufacturing': 'Manufacturing',
    'nav.employees': 'Employees & Payroll',
    'nav.accounting': 'Accounting',
    'nav.cash_management': 'Cash & Shifts',
    'nav.vouchers': 'Vouchers',
    'nav.vat_center': 'VAT Center',
    'nav.reports': 'Reports & Compliance',
    'nav.invoice_history': 'Invoice History',
    'nav.invoice_archive': 'Invoice Archive',
    'nav.zatca': 'ZATCA Compliance',
    'nav.subscription': 'Subscription',
    'nav.payment_methods': 'Payment Methods',
    'nav.pricing': 'Plans & Pricing',
    'nav.legal': 'Legal Documents',
    'nav.branches': 'Branches',
    'nav.approvals': 'Approvals',
    'nav.audit_logs': 'Audit Logs',
    'nav.backup': 'Backup',
    'nav.csv_import': 'CSV Import',
    'nav.settings': 'Settings',
    'nav.system_health': 'System Health',
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.print': 'Print',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.loading': 'Loading...',
    'common.no_data': 'No data available',
    'common.total': 'Total',
    'common.status': 'Status',
    'common.date': 'Date',
    'common.amount': 'Amount',
    'common.description': 'Description',
    'common.reference': 'Reference',
    'common.notes': 'Notes',
    'common.actions': 'Actions',
    'common.view': 'View',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.success': 'Success',
    'common.error': 'Error',
    'common.warning': 'Warning',
    'common.info': 'Info',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.sar': 'SAR',
    'common.undo': 'Undo',
    'common.void': 'Void',
    'common.reverse': 'Reverse',
    'common.reopen': 'Reopen',
    'common.approve': 'Approve',
    'common.reject': 'Reject',
    'common.pending': 'Pending',
    'common.approved': 'Approved',
    'common.rejected': 'Rejected',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.all': 'All',
    'common.new': 'New',
    'common.draft': 'Draft',
    'common.posted': 'Posted',
    'common.cancelled': 'Cancelled',
    'common.reversed': 'Reversed',
    'common.refresh': 'Refresh',
    'common.details': 'Details',
    'common.name': 'Name',
    'common.type': 'Type',
    'common.branch': 'Branch',
    'common.today': 'Today',
    'common.week': 'This Week',
    'common.month': 'This Month',
    'common.quarter': 'This Quarter',
    'common.year': 'This Year',
    'common.connected': 'Connected',
    'common.disconnected': 'Disconnected',
    'common.online': 'Online',
    'common.offline': 'Offline',
    'common.open': 'Open',
    'common.closed': 'Closed',
    'common.suspended': 'Suspended',
    'common.exit': 'Exit',
    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.enable': 'Enable',
    'common.disable': 'Disable',
    'common.transactions': 'Transactions',
    'common.sales': 'Sales',
    'common.purchases': 'Purchases',
    'common.profit': 'Profit',
    'common.customers': 'Customers',
    'common.stock': 'Stock',
    'common.invoice': 'Invoice',
    'common.invoices': 'Invoices',
    'common.payment': 'Payment',
    'common.cash': 'Cash',
    'common.card': 'Card',
    'common.transfer': 'Transfer',
    'common.discount': 'Discount',
    'common.vat': 'VAT',
    'common.subtotal': 'Subtotal',
    'common.grand_total': 'Grand Total',
    'common.change': 'Change',
    'common.paid': 'Paid',
    'common.refunded': 'Refunded',
    'common.completed': 'Completed',
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome — Nexora ERP, ZATCA Phase 2 Compliant',
    'dashboard.date_range': 'Date Range',
    'dashboard.detailed_reports': 'Detailed Reports',
    'dashboard.quick_access': 'Quick Access to Detailed Reports',
    'dashboard.metrics.today_sales': "Today's Sales",
    'dashboard.metrics.purchases': 'Purchases',
    'dashboard.metrics.net_profit': 'Net Profit',
    'dashboard.metrics.vat': 'VAT',
    'dashboard.metrics.customers': 'Total Customers',
    'dashboard.metrics.low_stock': 'Low Stock',
    'dashboard.metrics.invoices_today': 'inv.',
    'dashboard.metrics.purchase_orders': 'PO',
    'dashboard.metrics.profit_margin': 'Profit Margin',
    'dashboard.metrics.zatca_compliant': 'ZATCA Compliant',
    'dashboard.metrics.registered_customers': 'registered customers',
    'dashboard.metrics.below_minimum': 'items below minimum',
    'dashboard.metrics.needs_order': 'Needs Order',
    'dashboard.metrics.good_level': 'Good Level',
    'dashboard.metrics.new': 'new',
    'dashboard.metrics.details': 'Details',
    'dashboard.drill.sales_title': "Today's Sales Details",
    'dashboard.drill.purchases_title': 'Purchases Details',
    'dashboard.drill.profit_title': 'Profitability Analysis',
    'dashboard.drill.vat_title': 'VAT Details',
    'dashboard.drill.customers_title': 'Customer Analysis',
    'dashboard.drill.stock_title': 'Stock Alerts',
    'dashboard.drill.view_full_report': 'View Full Report',
    'dashboard.reports.sales': 'Sales Report',
    'dashboard.reports.sales_sub': 'Sales & Profits',
    'dashboard.reports.purchases': 'Purchases Report',
    'dashboard.reports.purchases_sub': 'Suppliers & Orders',
    'dashboard.reports.inventory': 'Inventory Report',
    'dashboard.reports.inventory_sub': 'Movement & Analysis',
    'dashboard.reports.vat': 'VAT Report',
    'dashboard.reports.vat_sub': 'Output & Input Tax',
    'dashboard.reports.financial': 'Financial Report',
    'dashboard.reports.financial_sub': 'Revenue & Expenses',
    'dashboard.reports.payroll': 'Payroll Report',
    'dashboard.reports.payroll_sub': 'Salaries & Insurance',
    // Dashboard Accordion sections
    'dashboard.section.overview': 'Overview — Key Metrics',
    'dashboard.section.charts': 'Charts & Compliance',
    'dashboard.section.activity': 'Activity & Stock',
    'dashboard.section.reports': 'Quick Report Access',
    // POS
    'pos.title': 'Point of Sale',
    'pos.search_products': 'Search product or barcode...',
    'pos.cart': 'Cart',
    'pos.cart_empty': 'Cart is empty',
    'pos.add_to_cart': 'Add to Cart',
    'pos.checkout': 'Checkout',
    'pos.payment': 'Payment',
    'pos.cash_payment': 'Cash Payment',
    'pos.card_payment': 'Card Payment',
    'pos.stc_payment': 'STC Pay',
    'pos.split_payment': 'Split Payment',
    'pos.bank_transfer': 'Bank Transfer',
    'pos.credit': 'Credit',
    'pos.amount_paid': 'Amount Paid',
    'pos.change': 'Change',
    'pos.print_receipt': 'Print Receipt',
    'pos.new_sale': 'New Sale',
    'pos.hold': 'Hold',
    'pos.recall': 'Recall',
    'pos.discount': 'Discount',
    'pos.customer': 'Customer',
    'pos.cash_customer': 'Cash Customer',
    'pos.select_customer': 'Select Customer',
    'pos.barcode': 'Barcode',
    'pos.qty': 'Qty',
    'pos.unit_price': 'Unit Price',
    'pos.subtotal': 'Subtotal',
    'pos.vat': 'VAT',
    'pos.total': 'Total',
    'pos.invoice_no': 'Invoice No.',
    'pos.categories.all': 'All',
    'pos.out_of_stock': 'Out of Stock',
    'pos.low_stock': 'Low Stock',
    'pos.format_80mm': '80mm Receipt',
    'pos.format_a4': 'A4 Invoice',
    'pos.sale_complete': 'Sale Complete',
    'pos.connected': 'Connected',
    'pos.search_shortcut': 'Search',
    'pos.pay_shortcut': 'Pay',
    'pos.clear_shortcut': 'Clear',
    'pos.return_to_terminals': 'Return to Terminals',
    // POS Terminals
    'terminals.title': 'POS Terminals',
    'terminals.add': 'Add Terminal',
    'terminals.edit': 'Edit Terminal',
    'terminals.delete': 'Delete Terminal',
    'terminals.code': 'Terminal Code',
    'terminals.name_ar': 'Name (Arabic)',
    'terminals.name_en': 'Name (English)',
    'terminals.branch': 'Branch',
    'terminals.type': 'Terminal Type',
    'terminals.printer': 'Printer',
    'terminals.payment_methods': 'Payment Methods',
    'terminals.shift_status': 'Shift Status',
    'terminals.today_sales': "Today's Sales",
    'terminals.today_transactions': "Today's Transactions",
    'terminals.last_activity': 'Last Activity',
    'terminals.open_cashier': 'Open Cashier',
    'terminals.cashier_url': 'Cashier URL',
    'terminals.type.standard': 'Standard Cashier',
    'terminals.type.mobile': 'Mobile POS',
    'terminals.type.kiosk': 'Self-Service Kiosk',
    'terminals.type.drive_thru': 'Drive-Thru',
    'terminals.printer.thermal_80mm': 'Thermal 80mm',
    'terminals.printer.thermal_58mm': 'Thermal 58mm',
    'terminals.printer.a4': 'A4 Printer',
    'terminals.printer.none': 'No Printer',
    'terminals.shift.open': 'Shift Open',
    'terminals.shift.closed': 'Shift Closed',
    'terminals.shift.suspended': 'Shift Suspended',
    'terminals.payment.cash': 'Cash',
    'terminals.payment.card': 'Card',
    'terminals.payment.stc': 'STC Pay',
    'terminals.payment.mada': 'Mada',
    'terminals.payment.apple_pay': 'Apple Pay',
    'terminals.name_required': 'Terminal Arabic name is required',
    'terminals.limit_reached': 'You have reached the terminal limit for your plan',
    'terminals.upgrade': 'Upgrade Plan',
    // POS Transactions
    'pos_tx.title': 'POS Transactions',
    'pos_tx.search': 'Search by invoice number or customer...',
    'pos_tx.all_statuses': 'All Statuses',
    'pos_tx.all_payments': 'All Payment Methods',
    'pos_tx.invoice_no': 'Invoice No.',
    'pos_tx.customer': 'Customer',
    'pos_tx.cashier': 'Cashier',
    'pos_tx.items': 'items',
    'pos_tx.payment_method': 'Payment Method',
    'pos_tx.total': 'Total',
    'pos_tx.vat': 'VAT',
    'pos_tx.discount': 'Discount',
    'pos_tx.net': 'Net',
    'pos_tx.status.completed': 'Completed',
    'pos_tx.status.pending': 'Pending',
    'pos_tx.status.cancelled': 'Cancelled',
    'pos_tx.status.refunded': 'Refunded',
    'pos_tx.payment.cash': 'Cash',
    'pos_tx.payment.card': 'Card',
    'pos_tx.payment.stc': 'STC Pay',
    'pos_tx.payment.split': 'Split',
    'pos_tx.summary.total_sales': 'Total Sales',
    'pos_tx.summary.total_vat': 'Total VAT',
    'pos_tx.summary.total_discount': 'Total Discounts',
    'pos_tx.summary.transactions': 'Transactions',
    // Shifts
    'shift.open': 'Open Shift',
    'shift.close': 'Close Shift',
    'shift.suspend': 'Suspend Shift',
    'shift.resume': 'Resume Shift',
    'shift.opening_balance': 'Opening Balance',
    'shift.closing_balance': 'Closing Balance',
    'shift.cash_sales': 'Cash Sales',
    'shift.card_sales': 'Card Sales',
    'shift.transfer_sales': 'Transfer Sales',
    'shift.returns': 'Returns',
    'shift.discounts': 'Discounts',
    'shift.expenses': 'Expenses',
    'shift.deposits': 'Deposits',
    'shift.withdrawals': 'Withdrawals',
    'shift.expected_cash': 'Expected Cash',
    'shift.actual_cash': 'Actual Cash',
    'shift.variance': 'Variance',
    'shift.manager_pin': 'Manager PIN',
    'shift.cashier': 'Cashier',
    'shift.branch': 'Branch',
    'shift.open_time': 'Open Time',
    'shift.close_time': 'Close Time',
    'shift.report': 'Shift Report',
    'shift.history': 'Shift History',
    'shift.status.open': 'Open',
    'shift.status.closed': 'Closed',
    'shift.status.suspended': 'Suspended',
    // Accounting
    'accounting.chart_of_accounts': 'Chart of Accounts',
    'accounting.journal_entries': 'Journal Entries',
    'accounting.general_ledger': 'General Ledger',
    'accounting.trial_balance': 'Trial Balance',
    'accounting.balance_sheet': 'Balance Sheet',
    'accounting.income_statement': 'Income Statement',
    'accounting.cash_flow': 'Cash Flow',
    'accounting.assets': 'Assets',
    'accounting.liabilities': 'Liabilities',
    'accounting.equity': 'Equity',
    'accounting.revenue': 'Revenue',
    'accounting.expenses': 'Expenses',
    'accounting.net_profit': 'Net Profit',
    'accounting.receivables': 'Receivables',
    'accounting.payables': 'Payables',
    'accounting.cash_position': 'Cash Position',
    'accounting.post': 'Post',
    'accounting.unpost': 'Unpost',
    'accounting.batch_post': 'Batch Post',
    'accounting.debit': 'Debit',
    'accounting.credit': 'Credit',
    // Documents
    'doc.invoice': 'Invoice',
    'doc.quotation': 'Quotation',
    'doc.purchase_order': 'Purchase Order',
    'doc.sales_order': 'Sales Order',
    'doc.receipt_voucher': 'Receipt Voucher',
    'doc.payment_voucher': 'Payment Voucher',
    'doc.journal_voucher': 'Journal Voucher',
    'doc.cash_voucher': 'Cash Voucher',
    'doc.bank_voucher': 'Bank Voucher',
    'doc.prepared_by': 'Prepared By',
    'doc.approved_by': 'Approved By',
    'doc.received_by': 'Received By',
    'doc.signature': 'Signature',
    'doc.stamp': 'Stamp',
    'doc.qr_code': 'QR Code',
    'doc.vat_number': 'VAT Number',
    'doc.cr_number': 'CR Number',
    'doc.contract_number': 'Contract No.',
    'doc.vehicle_number': 'Vehicle No.',
    'doc.sales_rep': 'Sales Representative',
    'doc.customer_ref': 'Customer Reference',
    'doc.internal_ref': 'Internal Reference',
    // Roles
    'role.super_admin': 'Platform Owner',
    'role.admin': 'System Admin',
    'role.manager': 'Store Manager',
    'role.accountant': 'Accountant',
    'role.cashier': 'Cashier',
    'role.viewer': 'Viewer',
    'role.employee': 'Employee',
    // Company Portal
    'portal.title': 'Company Portal',
    'portal.workspace': 'Workspace',
    'portal.company_profile': 'Company Profile',
    'portal.company_url': 'Company URL',
    'portal.branding': 'Branding',
    // Validation
    'validation.required_field': 'This field is required',
    'validation.invalid_amount': 'Invalid amount',
    'validation.balance_mismatch': 'Balance sheet mismatch: Assets ≠ Liabilities + Equity',
    'validation.duplicate_posting': 'This entry has already been posted',
    'validation.shift_already_open': 'A shift is already open',
    'validation.no_open_shift': 'No open shift found',
    'validation.wrong_pin': 'Incorrect manager PIN',
    // Demo
    'demo.banner': 'Demo Mode — View Only. No create, edit, delete or configure allowed.',
    'demo.view_only': 'View Only',
  },
};

export function getTranslations(lang: Language): (key: string, fallback?: string) => string {
  const dict = translations[lang] || translations['ar'];
  return (key: string, fallback?: string) => dict[key] ?? fallback ?? key;
}

// ── React Hook ────────────────────────────────────────────────────────────────
export function useTranslation() {
  const [lang, setLangState] = useState<Language>('ar');

  useEffect(() => {
    setLangState(getStoredLanguage());
    const handler = () => setLangState(getStoredLanguage());
    window.addEventListener('nexora_lang_change', handler);
    return () => window.removeEventListener('nexora_lang_change', handler);
  }, []);

  const setLang = useCallback((next: Language) => {
    setStoredLanguage(next);
    setLangState(next);
    window.dispatchEvent(new Event('nexora_lang_change'));
  }, []);

  const t = useCallback(
    (key: string, fallback?: string) => getTranslations(lang)(key, fallback),
    [lang]
  );

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  return { t, lang, setLang, dir };
}
